import {
  cloneElement,
  isValidElement,
  type ReactNode,
  type ReactElement,
  useId,
} from "react";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Search,
  LoaderCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError } from "@/lib/api";
import { FieldContext } from "./field-context";

export function PageHeading({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-heading">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && (
          <p className="text-muted-foreground mt-2 leading-relaxed">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <Search size={24} aria-hidden />
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}
export function QueryError({
  error,
  retry,
}: {
  error: unknown;
  retry?: () => void;
}) {
  return (
    <div className="error-state" role="alert">
      <AlertCircle size={20} aria-hidden />
      <div>
        <p>{error instanceof Error ? error.message : "加载未能完成"}</p>
        {error instanceof ApiError && error.requestId && (
          <p className="text-sm mt-2">请求编号：{error.requestId}</p>
        )}
        {retry && (
          <Button className="mt-3" variant="outline" onClick={retry}>
            重新加载
          </Button>
        )}
      </div>
    </div>
  );
}
export function LoadingState() {
  return (
    <div className="space-y-4 py-8" aria-label="正在加载" role="status">
      <span className="sr-only">正在加载</span>
      {[1, 2, 3].map((n) => (
        <Skeleton key={n} className="h-16 w-full" />
      ))}
    </div>
  );
}
export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  const id = useId();
  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        "aria-labelledby": id,
        "aria-describedby": hint ? `${id}-hint` : undefined,
      })
    : children;
  return (
    <FieldContext.Provider
      value={{ labelId: id, hintId: hint ? `${id}-hint` : undefined }}
    >
      <div className="field" role="group" aria-labelledby={id}>
        <div id={id} className="field-label">
          {label}
        </div>
        {control}
        {hint && (
          <p id={`${id}-hint`} className="field-hint">
            {hint}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}
export function Pagination({
  page,
  total,
  pageSize,
  onPage,
}: {
  page: number;
  total: number;
  pageSize: number;
  onPage: (n: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <nav className="pagination" aria-label="翻页">
      <span className="text-muted-foreground">
        共 {total} 项 · 第 {page} / {pages} 页
      </span>
      <div className="flex gap-2">
        <Button
          aria-label="上一页"
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          <ArrowLeft size={16} />
        </Button>
        <Button
          aria-label="下一页"
          variant="outline"
          size="sm"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        >
          <ArrowRight size={16} />
        </Button>
      </div>
    </nav>
  );
}
export function SavingLabel({ pending }: { pending: boolean }) {
  return pending ? (
    <>
      <LoaderCircle className="animate-spin" size={16} />
      保存中…
    </>
  ) : (
    <>保存记录</>
  );
}
export function formatDate(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Shanghai",
  }).format(new Date(value));
}
